import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, TextField, Typography, useTheme, Select, MenuItem, FormHelperText } from '@mui/material'
import { CancelOutlined, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Form, Formik, ErrorMessage, Field } from 'formik'

import { discussionAttachmentUploader, onDiscussionFilePasteOrDrop } from '../../utils/custom-editor-utilities'
import { useDiscussionForum } from '../../store/discussionforum.store'
import { useUserStore } from '../../store/user.store'
import CustomWysiwyg from '../common/CustomWysiwyg'
import { Icons } from '../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../service/api'
import * as Yup from 'yup'
import DataLoadingSpinner from '../common/DataLoadingSpinner'
import CustomChip from '../common/CustomChip'

let filesList = [],
    editorRef

const filePasteOrDropHandler = async ( file, mime ) => {
    const urlDetails = await onDiscussionFilePasteOrDrop( file, mime )
    filesList.push( urlDetails.src )
    return urlDetails
}

const handleFileInput = async ( type ) => {
    const input = document.createElement( 'input' )

    input.setAttribute( 'type', 'file' )
    input.setAttribute( 'accept', type === 'image' ? 'image/png,image/jpeg' : 'video/mp4,video/webm' )
    input.click()

    input.onchange = async () => {
        var file = input.files[0]
        if ( type === 'image' && file.size / 1024 > 2 * 1024 ) {
            toast( "File too large. Max size: 2MB" )
            return ""
        }
        if ( type === 'video' && file.size / 1024 > 5 * 1024 ) {
            toast( "File too large. Max size: 5MB" )
            return ""
        }

        if ( type === 'image' && ( !file.type?.includes( 'image' ) || !['jpeg', 'jpg', 'png'].includes( file.name?.split( '.' )?.pop()?.toLowerCase() ) ) ) {

            toast( 'Invalid image format!' )
            return ''
        }
        if ( type === 'video' && ( !file.type?.includes( 'video' ) || !['mp4', 'webm'].includes( file.name?.split( ',' )?.pop()?.toLowerCase() ) ) ) {
            toast( 'Invalid video format!' )
            return ''
        }

        try {
            if ( editorRef.current ) {
                var url = await discussionAttachmentUploader( type, file )
                filesList.push( url )
                if ( url ) {
                    const range = editorRef.current.getSelection()
                    editorRef.current.insertEmbed( range.index, type, url )
                    editorRef.current.setSelection( range.index + 1 )
                    setTimeout( () => {
                        editorRef.current.focus()
                    }, 10 )
                }
            }
        } catch ( err ) {
            console.log( err )
        }
    }
}

const modules = {
    toolbar: {
        container: [['bold', 'italic', 'underline', 'strike', 'blockquote'], [{ size: ['normal', 'small', 'large'] }], [{ align: [] }], [{ script: 'sub' }, { script: 'super' }], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'image', 'video'], ['clean'], ['code-block']],
        handlers: {
            image: async () => await handleFileInput( 'image' ),
            video: async () => await handleFileInput( 'video' ),
        },
    },
}
const CreateDiscussionDialog = ( { state, setState, getData } ) => {
    const [selectedSubjectsIds, setSelectedSubjectsIds] = useState( [] )
    const [postingDiscussion, setPostingDiscussion] = useState( false )
    const [loadingSubjects, setLoadingSubjects] = useState( true )
    const [subjectFilter, setSubjectFilter] = useState( 1 )
    const [subjectFilterOptions, setSubjectFilterOptions] = useState( [] )

    const DiscussionForumStore = useDiscussionForum()
    const UserStore = useUserStore()

    const subjectsInputContainerRef = useRef()
    editorRef = useRef()

    const navigate = useNavigate()

    const { palette, border } = useTheme()

    const [searchParams, setSearchParams] = useSearchParams( { subjectSearchText: '' } )
    const subjectSearchText = searchParams.get( 'subjectSearchText' ) || ''

    const initialValues = {
        title: '',
        content: '',
        access: 'subject_specific',
        access_groups: [],
        categories: [],
    }

    const validationSchema = Yup.object().shape( {
        title: Yup.string().required( 'Discussion title is required' ).max( 300, 'Max length 300 characters' ),
        content: Yup.string()
            .required( 'Content is required' )
            .test( 'CHECK_CONTENT', 'Please enter discussion content', ( val ) => {
                if ( !val || val?.trim() === '' || val?.trim()?.replace( /\s/gi, '' ) === `<p></p>` || val?.trim()?.replace( /\s/gi, '' ) === `<p><br></p>` ) return false
                return true
            } ),
        access: Yup.string().required( 'Access type is required' ).oneOf( ['global', 'subject_specific'] ),
        access_groups: Yup.array().required( 'Access groups are required' ).min( 1, 'Discussion should have at least one access group' ),
        categories: Yup.array().required( 'Categories are required' ).min( 1, 'Discussion should have at least one category' ),
    } )

    const subjectsFilterFunc = ( s ) => {
        const subjectSearchTextPattern = new RegExp( subjectSearchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( UserStore.getUser.user_role === 'STUDENT' ) {
            if ( UserStore.getUserSubjectsDetails.yearWise )
                return +s.current_year === +subjectFilter && ( s.subject_name?.match( subjectSearchTextPattern ) || s.subject_code?.match( subjectSearchTextPattern ) )
            else
                return +s.current_sem === +subjectFilter && ( s.subject_name?.match( subjectSearchTextPattern ) || s.subject_code?.match( subjectSearchTextPattern ) )
        } else {
            return s.ac_year === subjectFilter && ( s.subject_name?.match( subjectSearchTextPattern ) || s.subject_code?.match( subjectSearchTextPattern ) )
        }
    }

    const setSearchParamValue = ( key, val ) => {
        setSearchParams(
            ( prev ) => {
                prev.set( key, val )
                return prev
            },
            { replace: true }
        )
    }

    const setCategories = ( setFieldValue, val ) => {
        setFieldValue( 'categories', val )
    }

    const handleClose = ( resetForm = null ) => {
        if ( resetForm )
            resetForm()
        setSelectedSubjectsIds( [] )
        setState( false )
        setSearchParamValue( 'subjectSearchText', '' )
    }

    const handleKeyUp = ( e, setFieldValue, categories ) => {
        if ( ( e.key === 'Enter' || e.key === 'Tab' ) && e.target.value.trim() !== '' ) {
            e.preventDefault()
            setCategories( setFieldValue, [...categories, e.target.value] )
            e.target.value = ''
        }
    }

    const removeCategory = ( index, setFieldValue, categories ) => {
        let copy = [...categories]
        copy.splice( index, 1 )
        setCategories( setFieldValue, copy )
    }

    const toggleSubjectInclusion = ( sub, value, setFieldValue ) => {
        if ( selectedSubjectsIds.includes( sub.subject_id ) ) {
            let index = null
            for ( let k = 0; k < value.length; k++ ) {
                const currentSub = value[k]
                if ( sub.subject_id === currentSub.subject_id ) {
                    index = k
                    break
                }
            }
            if ( index > -1 ) {
                removeSubject( index, value, setFieldValue )
            }
        } else {
            setFieldValue( 'access_groups', [...value, sub] )
            setSelectedSubjectsIds( [...value, sub].map( ( s ) => s.subject_id ) )
        }
    }

    const removeSubject = ( index, subjects, setFieldValue ) => {
        let copy = [...subjects]
        copy.splice( index, 1 )
        setFieldValue( 'access_groups', copy )
        setSelectedSubjectsIds( copy.map( ( s ) => s.subject_id ) )
    }

    const getSubjects = async () => {
        setLoadingSubjects( true )
        if ( UserStore.getErrorsStatuses?.subjects === true )
            await UserStore.fetchUserSubjects()
        const subjectsDetails = UserStore.getUserSubjectsDetails
        if ( UserStore.getUser.user_role === 'STUDENT' ) {
            if ( subjectsDetails.yearWise )
                setSubjectFilterOptions( subjectsDetails.years )
            else
                setSubjectFilterOptions( subjectsDetails.semesters )
        } else {
            setSubjectFilterOptions( UserStore.getUserAcademicYears.map( ac => ac.ac_year ) )
            setSubjectFilter( UserStore.getUserAcademicYears[0].ac_year )
        }
        setLoadingSubjects( false )
    }

    const handleSemesterChange = ( e ) => {
        setSubjectFilter( e.target.value )
    }

    const cachedGetSubjects = useCallback( getSubjects, [UserStore] )

    const uniqueSubjects = useMemo( () => {
        const uniqueIds = {}
        const filtered = []
        for ( const subject of UserStore.getUserSubjectsDetails?.currentSubjects ) {
            if ( !uniqueIds[subject.subject_id] ) {
                filtered.push( subject )
                uniqueIds[subject.subject_id] = true
            }
        }
        return filtered
    }, [UserStore.getUserSubjectsDetails?.currentSubjects] )

    const deleteFileFromSpaces = async ( url ) => {
        try {
            await api.discussionForum.deleteFile( { url } )
            const fileUrlIndex = filesList.indexOf( url )
            if ( fileUrlIndex > -1 ) filesList.splice( fileUrlIndex, 1 )
        } catch ( err ) { }
    }

    const handleEditorValueChange = async ( val, setFieldValue, previousValue ) => {
        setFieldValue( 'content', val )
        if ( val.length !== previousValue.length ) {
            for ( let i = 0; i < filesList.length; i++ ) {
                const url = filesList[i]
                if ( !val.includes( url ) ) {
                    deleteFileFromSpaces( decodeURIComponent( url ) )
                }
            }
        }
    }

    const createDiscussion = async ( values, { resetForm } ) => {
        setPostingDiscussion( true )
        for ( const url of filesList ) {
            if ( !values?.content?.includes( url ) ) {
                console.log( 'Deleting: ', url )
                deleteFileFromSpaces( decodeURIComponent( url ) )
            }
        }
        const { success, data } = await DiscussionForumStore.createDiscussion( values )
        if ( success ) {
            await getData()
            toast(
                <span>
                    Successfully created new discussion: <strong>{values.title}</strong>{' '}
                </span>
            )
            handleClose( resetForm )
            navigate( `${UserStore.getUser.user_role === 'STUDENT' ? '/student' : '/faculty'}/discussions/${data._id}` )
        }
        setPostingDiscussion( false )
    }

    useEffect( () => {
        const getData = async () => {
            await cachedGetSubjects()
        }
        getData()
    }, [cachedGetSubjects] )

    return (
        <Dialog open={state} slotProps={{
            paper: { sx: { background: palette.form.formCardBg, width: '90%', maxWidth: '850px' } }
        }}>
            <Box display='flex' alignItems='center' justifyContent='space-between' gap='20px' padding='10px 20px' borderBottom='1px solid #d3d3d3'>
                <Box   >
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Create a new discussion</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Initiate a peer discussion by presenting a new topic and offering your insights to get the conversation started.</Typography>
                </Box>
                <IconButton onClick={() => handleClose()}>{Icons.default.CloseIcon}</IconButton>
            </Box>
            <DialogContent sx={{ padding: 0 }}>
                <Box>
                    <Formik initialValues={initialValues} onSubmit={createDiscussion} validationSchema={validationSchema}>
                        {( { values, setFieldValue, resetForm } ) => {
                            return (
                                <Form>
                                    <Box borderRadius='5px' padding='20px' display='flex' flexDirection='column' gap='10px'>
                                        <Box>
                                            <Typography component="label" display="block" htmlFor='dicussion-title' variant='subtitle2' >
                                                Discussion title
                                            </Typography>
                                            <Typography component="label" display="block" htmlFor='dicussion-title' marginTop='-5px' gutterBottom variant='body2' fontSize='12px' color='textSecondary'>
                                                Summarize the discussion topic in the short title.
                                            </Typography>
                                            <Field autoFocus id="discussion-title" as={TextField} placeholder='Eg: What are your thoughts on...' type='text' size='small' name='title' variant='outlined' fullWidth />
                                            <FormHelperText>
                                                <ErrorMessage name='title' />{' '}
                                            </FormHelperText>
                                        </Box>
                                        <Box>
                                            <Typography component="label" display="block" htmlFor='dicussion-content' variant='subtitle2' >
                                                Discussion content
                                            </Typography>
                                            <Typography component="label" display="block" htmlFor='dicussion-content' marginTop='-5px' gutterBottom variant='body2' fontSize='12px' color='textSecondary'>
                                                Describe the discussion topic in detail.
                                            </Typography>
                                            <Box>
                                                <Box bgcolor={palette.form.inputBg}>
                                                    <CustomWysiwyg
                                                        id="discussion-content"
                                                        dropBox={
                                                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" className="animation-shake">
                                                                <Typography variant="h6" component="p" textAlign="center" >Drop here!</Typography>
                                                                <Typography variant="subtitle1" component="span" textAlign="center" fontSize="12px">Image max size: 2MB</Typography>
                                                                <Typography variant="subtitle1" component="span" textAlign="center" fontSize="12px">Video max size: 5MB</Typography>
                                                            </Box>
                                                        }
                                                        filePasteOrDropHandler={filePasteOrDropHandler}
                                                        height='200px'
                                                        onChange={( e ) => handleEditorValueChange( e, setFieldValue, values.content )}
                                                        setParentEditorRef={( refVal ) => {
                                                            editorRef.current = refVal?.current
                                                        }}
                                                        value={values.content}
                                                        modules={modules}
                                                        placeholder='Share your thoughts...'
                                                    />
                                                </Box>
                                                <FormHelperText>
                                                    <ErrorMessage name='content' />
                                                </FormHelperText>
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography gutterBottom component="label" display="block" htmlFor='dicussion-categories' variant='subtitle2' >
                                                Specify discussion categories
                                            </Typography>
                                            <Box flexWrap='wrap' border={`2px dotted ${palette.borderColor}`} padding="10px" alignItems='center' gap='5px' borderRadius='5px'  >
                                                <TextField id="dicussion-categories" fullWidth size='small' onKeyDown={( e ) => handleKeyUp( e, setFieldValue, values.categories )} type='text' placeholder='Type categories and press enter to add.' />
                                                {values.categories.length > 0 && <Box marginTop="10px" display="flex" gap="5px" flexWrap="wrap" alignItems="center">
                                                    {values.categories.map( ( category, index ) => ( <Box key={index} bgcolor={palette.contentBg} fontSize="12px" display='flex' gap='10px' borderRadius='5px' width='fit-content' alignItems='center' padding='2px 5px 2px 10px' border={border[1]} >
                                                        <Typography variant='subtitle2' fontSize="12px" component="em"> {category} </Typography>
                                                        <IconButton size='small' onClick={() => removeCategory( index, setFieldValue, values.categories )} > <CancelOutlined fontSize='small' /> </IconButton>
                                                    </Box> ) )}
                                                </Box>}
                                                {values.categories.length === 0 && <Typography textAlign="center" padding="20px" variant='body2' fontSize="12px" >
                                                    No categories added yet. Add categories to help organize discussions.
                                                </Typography>}
                                            </Box>
                                            <FormHelperText>
                                                <ErrorMessage name='categories' />
                                            </FormHelperText>
                                        </Box>

                                        {values.access === 'subject_specific' && (
                                            <Box>
                                                <Typography variant='subtitle2' > Select access groups(subjects) </Typography>
                                                <Typography marginTop='-5px' gutterBottom variant='body2' fontSize='12px' color='textSecondary' >
                                                    Only selected groups can participate in the discussion.
                                                </Typography>

                                                <Box ref={subjectsInputContainerRef} border={`2px dotted ${palette.borderColor}`} padding="10px" gap='10px' borderRadius='5px'>
                                                    {values.access_groups.length > 0 && <Box display="flex" gap="10px" flexWrap="wrap" marginBottom="10px">
                                                        {values.access_groups.map( ( selSub, index ) => (
                                                            <Box key={selSub.subject_id} bgcolor={palette.contentBg} display='flex' gap='10px' width='fit-content' alignItems='center' padding='0 5px' borderRadius="5px" border={border[1]} >
                                                                <Box>
                                                                    <Typography component="em" textTransform="capitalize" fontSize="12px" variant='subtitle2'>
                                                                        {selSub.subject_name?.toLowerCase()}
                                                                    </Typography>
                                                                    <Typography component="em" display="block" fontSize='10px'>
                                                                        {selSub.subject_code}
                                                                    </Typography>
                                                                </Box>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() =>
                                                                        removeSubject(
                                                                            index,
                                                                            values.access_groups,
                                                                            setFieldValue
                                                                        )
                                                                    }
                                                                >
                                                                    <CancelOutlined fontSize='small' />
                                                                </IconButton>
                                                            </Box>
                                                        ) )}
                                                        {values.access_groups.length === 0 && <Typography textAlign="center" padding="20px" variant='body2' fontSize="12px" >
                                                            No access groups added yet. Add subjects to specify access.
                                                        </Typography>}
                                                    </Box>}
                                                    <Box sx={{ zIndex: 5 }} display="flex" gap="10px" >
                                                        <Select sx={{ fontSize: "14px" }} size='small' variant='outlined' onChange={handleSemesterChange} value={subjectFilter}>
                                                            {subjectFilterOptions.map( ( filterOption, i ) => (
                                                                <MenuItem dense sx={{ fontSize: '14px' }} value={filterOption} key={filterOption}>
                                                                    {UserStore.getUser.user_role === 'STUDENT' ? UserStore.getUserSubjectsDetails ? "Year" : "Semester" : "Academic Year"} {filterOption}
                                                                </MenuItem>
                                                            ) )}
                                                        </Select>
                                                        <TextField onChange={( e ) => setSearchParamValue( 'subjectSearchText', e.target.value )} size='small' fullWidth type='text' placeholder='Search subjects...' />
                                                    </Box>
                                                    <Box maxHeight="200px" marginTop="10px" className="custom-scrollbar" overflow="auto">
                                                        {uniqueSubjects
                                                            .filter( subjectsFilterFunc )
                                                            .map( ( sub ) => {
                                                                return (
                                                                    // <FormControlLabel sx={{ "&:hover": { background: palette.rowHover }, borderBottom: "1px solid #d3d3d3", margin: 0, padding: 0 }} size="small" onChange={( e ) => toggleSubjectInclusion( e, sub, values.access_groups, setFieldValue )} control={<Checkbox sx={{ height: "40px", width: "40px", transform: "scale(0.9)" }} />} label={`${sub.subject_name}(${sub.subject_name_short})`} />
                                                                    <Box key={sub.subject_id}>
                                                                        <Box
                                                                            onClick={() => toggleSubjectInclusion( sub, values.access_groups, setFieldValue )}
                                                                            display='flex'
                                                                            gap='10px'
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                '&:hover': {
                                                                                    background: palette.rowHover,
                                                                                },
                                                                            }}
                                                                            padding='2px 10px'
                                                                            alignItems='center'
                                                                            key={sub.subject_id}
                                                                            borderRadius="5px"
                                                                        // border={border[1]}
                                                                        >
                                                                            {!selectedSubjectsIds.includes( sub.subject_id ) ? (
                                                                                <CheckBoxOutlineBlank
                                                                                    sx={{
                                                                                        color: 'darkgrey',
                                                                                        '&:hover': {
                                                                                            color: 'GrayText',
                                                                                        },
                                                                                    }}
                                                                                    fontSize='small'
                                                                                />
                                                                            ) : (
                                                                                <CheckBox fontSize='small' />
                                                                            )}
                                                                            <Box display="flex" flexDirection="column">
                                                                                <Typography textTransform="capitalize" variant='subtitle2'>
                                                                                    {sub.subject_name?.toLowerCase()}
                                                                                </Typography>
                                                                                <Box display="flex" gap="5px" alignItems="center">
                                                                                    <CustomChip tooltipTitle='Subject code' value={sub.subject_code} />
                                                                                    <CustomChip tooltipTitle='Year' value={`Year: ${sub.year}`} />
                                                                                    {sub.sem && <CustomChip tooltipTitle='Semester' value={`Sem: ${sub.sem}`} />}
                                                                                </Box>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                )
                                                            } )}
                                                    </Box>
                                                    {!loadingSubjects && uniqueSubjects
                                                        .filter( subjectsFilterFunc ).length === 0 && <Typography display="block" borderRadius="5px" border={border[1]} textAlign="center" variant='subtitle2' component="em" fontSize="12px" padding="10px 20px"  >
                                                            Subjects are not available!
                                                        </Typography>}
                                                    {loadingSubjects && <Typography display="block" borderRadius="5px" border={border[1]} textAlign="center" variant='subtitle2' component="em" fontSize="12px" padding="10px 20px"  >
                                                        <DataLoadingSpinner size={20} waitingMessage="Loading subjects" />
                                                    </Typography>}
                                                </Box>
                                                <FormHelperText>
                                                    <ErrorMessage name='access_groups' />{' '}
                                                </FormHelperText>
                                            </Box>
                                        )}
                                        <Box display='flex' justifyContent="flex-end" marginTop="30px" gap='10px'>
                                            <Button disabled={postingDiscussion} startIcon={Icons.default.CloseIcon} onClick={() => handleClose( resetForm )} disableElevation variant='outlined' color='error'>
                                                Cancel
                                            </Button>
                                            <Button disabled={postingDiscussion} type='submit' variant='contained' disableElevation startIcon={postingDiscussion ? <CircularProgress size={14} /> : Icons.default.AddIcon} color='primary'>
                                                {postingDiscussion ? 'Creating discussion...' : 'Create Discussion'}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Form>
                            )
                        }}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default CreateDiscussionDialog
